import { FC } from 'react';
import { Box, Text, Grid } from 'src/components/ui';

export type ChannelType = 'online' | 'instore' | 'both' | 'digital';

export interface ChannelOption {
	type: ChannelType;
	name: string;
	description: string;
	icon: string;
}

export interface ChannelSelectorProps {
	onChannelSelect: (channelType: ChannelType) => void;
	selectedChannel?: ChannelType;
	type: ChannelType;
}

export interface ChannelOptionsGridProps {
	onChannelSelect: (channelType: ChannelType) => void;
	selectedChannel?: ChannelType;
}

const channelOptions: ChannelOption[] = [
	{
		type: 'online',
		name: 'You go to them',
		description: 'You travel to your customers to provide your service or deliver products',
		icon: '',
	},
	{
		type: 'instore',
		name: 'They come to you',
		description: 'Customers visit your store, office, or business location',
		icon: '',
	},
	{
		type: 'both',
		name: 'Both ways',
		description: 'Either from my location or to their homes',
		icon: '',
	},
	{
		type: 'digital',
		name: 'Digital Goods',
		description: 'Customers get digital access to your products or services',
		icon: '',
	}
];

/** Single channel option card */
const ChannelOptionCard: FC<{
	option: ChannelOption;
	onSelect: (type: ChannelType) => void;
	isCompact?: boolean;
}> = ({ option, onSelect, isCompact = false }) => {
	return (
		<Box
			border="1px solid"
			borderColor="transparent"
			borderRadius="xl"
			w="full"
			minH={isCompact ? "110px" : "140px"}
			textAlign="center"
			onClick={() => onSelect(option.type)}
			cursor="pointer"
			boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
			bg="white"
			_hover={{
				boxShadow: 'lg',
				borderColor: 'gray.300',
			}}
			transition="all 0.2s ease-in-out"
			overflow="hidden"
		>
			<Box bg="gray.50" py={isCompact ? 2 : 3} px={isCompact ? 2 : 4}>
				<Text color="gray.800" fontWeight="semibold" fontSize={isCompact ? "sm" : "md"}>
					{option.name}
				</Text>
			</Box>
			<Box bg="white" p={isCompact ? 2 : 4}>
				<Text color="gray.500" fontSize="xs">
					{option.description}
				</Text>
			</Box>
		</Box>
	);
};

/** Grid layout showing all channel options in 2x2 grid */
export const ChannelOptionsGrid: FC<ChannelOptionsGridProps> = ({
	onChannelSelect,
	selectedChannel,
}) => {
	return (
		<Grid
			templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }}
			gap={3}
			w="full"
			maxW="460px"
			mx="auto"
		>
			{channelOptions.map((option) => (
				<ChannelOptionCard
					key={option.type}
					option={option}
					onSelect={onChannelSelect}
					isCompact={true}
				/>
			))}
		</Grid>
	);
};

/** Single channel selector (legacy - used when showing one option at a time) */
const ChannelSelector: FC<ChannelSelectorProps> = ({
	onChannelSelect,
	selectedChannel,
	type,
}) => {
	// Find the specific channel option
	const option = channelOptions.find((opt) => opt.type === type);
	if (!option) return null;

	return (
		<Box
			border="1px solid"
			borderColor="transparent"
			borderRadius="xl"
			w="full"
			maxW="220px"
			minH="140px"
			mx="auto"
			textAlign="center"
			onClick={() => onChannelSelect(option.type)}
			cursor="pointer"
			boxShadow="0 0 4px rgba(0, 0, 0, 0.1)"
			bg="white"
			_hover={{
				boxShadow: 'lg',
				borderColor: 'gray.300',
			}}
			transition="all 0.2s ease-in-out"
			overflow="hidden"
		>
			<Box bg="gray.50" py={3} px={4}>
				<Text color="gray.800" fontWeight="semibold">
					{option.name}
				</Text>
			</Box>
			<Box bg="white" p={4}>
				<Text color="gray.500" fontSize="sm">
					{option.description}
				</Text>
			</Box>
		</Box>
	);
};

export default ChannelSelector;
